/**
 * `themed` — a colour that belongs to ONE site.
 *
 *   color: themed(t, '#666666', t.ink.secondary)
 *
 * Light keeps exactly the value the site has today. Dark names a role.
 *
 * WHY THIS EXISTS
 * On the day the migration tooling landed (2026-10-01) the phone app held 6,086
 * fixed colour values (`node web/scripts/theme-trace.cjs census --coverage`):
 *
 *   74%  already equal to a role's light value        → use the role
 *    9%  a role's colour at another alpha             → `withAlpha(role, a)` / `onGlass(a)`
 *   16%  match no role at all: 261 different colours, 145 of them used exactly once
 *
 * That last group is light's long tail — greys, tints, one-off accents. Light must
 * not change, so they cannot be snapped to the nearest role, and a palette with
 * 261 more roles that mostly serve one site each is not a palette. So the site keeps its
 * own light value and says which ROLE it is in dark. Every dark value still lives
 * in palette.ts, where it can be tuned and measured.
 *
 * RULES, enforced by the theme gate in a migrated file:
 *   - `dark` is a role (`t.…`, `withAlpha(t.…, a)`, `onGlass(a)`), never a literal.
 *     A dark colour written at a site is a dark colour nobody can tune.
 *     It may be a choice between roles (`lit ? t.ink.onCta : onGlass(0.3)`): one light
 *     colour can sit on two grounds that part ways in dark.
 *   - It is for a colour that was ALREADY in the file. New code names a role.
 *
 * And one that is judgement: when the same light value turns up in several files
 * doing the same job, it is a role that has not been named yet. Add it to the
 * palette instead (light = that value, dark = a measured one).
 */
import type { Palette } from './palette';

export function themed<T>(t: Palette, light: T, dark: T): T {
  return t.scheme === 'light' ? light : dark;
}
